import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { fetchSyncLogs, triggerSync } from "../../../../../api/admin";
import BackToDashboard from "../../../../../components/common/BackToDashboard";

const SOURCE_LABELS = {
  employees:   "Zoho Employees",
  contractors: "SP Contractors",
  macs:        "SP MACs",
  accounts:    "SP Accounts",
  sows:        "SP SOWs",
  snapshot:    "Monthly Snapshot",
};

const SOURCE_ORDER = ["macs", "accounts", "sows", "employees", "contractors", "snapshot"];

const STATUS_STYLES = {
  SUCCESS:     { dot: "#28B3B4", color: "#085041", darkColor: "#9FE1CB", bg: "rgba(40,179,180,0.1)",  darkBg: "rgba(40,179,180,0.18)" },
  FAILED:      { dot: "#E24B4A", color: "#791F1F", darkColor: "#F09595", bg: "rgba(226,75,74,0.08)", darkBg: "rgba(226,75,74,0.18)" },
  IN_PROGRESS: { dot: "#D6DF27", color: "#6B7300", darkColor: "#D6DF27", bg: "rgba(214,223,39,0.1)", darkBg: "rgba(214,223,39,0.15)" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const fmtDuration = (s) => {
  if (s == null) return "—";
  if (s < 60)   return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

// ── Status badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status, isDark }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.FAILED;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 20,
      letterSpacing: ".06em", textTransform: "uppercase",
      background: isDark ? s.darkBg : s.bg,
      color:      isDark ? s.darkColor : s.color,
      whiteSpace: "nowrap",
    }}>
      {status === "IN_PROGRESS"
        ? <CircularProgress size={8} sx={{ color: "inherit" }} />
        : <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
      }
      {status === "IN_PROGRESS" ? "In Progress" : status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
};

// ── Single source row (expandable) ────────────────────────────────────────────
const SourceRow = ({ source, runs, isDark, t, onSyncNow, syncing }) => {
  const [expanded, setExpanded] = useState(false);
  const latest = runs[0] ?? null;

  const th = {
    fontSize: 10, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase",
    color: t.text3, padding: "8px 12px", textAlign: "left",
    borderBottom: `1px solid ${t.border}`, whiteSpace: "nowrap",
    background: isDark ? "rgba(255,255,255,0.02)" : "rgba(3,81,77,0.02)",
  };
  const td = {
    fontSize: 12, color: t.text, padding: "8px 12px",
    borderBottom: `1px solid ${t.border}`, verticalAlign: "middle",
  };

  return (
    <>
      {/* Main source row */}
      <tr
        style={{ cursor: "pointer" }}
        onClick={() => setExpanded((e) => !e)}
      >
        {/* Expand chevron + source name */}
        <td style={{ ...td, width: 28, paddingRight: 0 }}>
          <span style={{
            display: "inline-block",
            transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform .15s",
            color: t.text3, fontSize: 10,
          }}>▶</span>
        </td>
        <td style={{ ...td, fontWeight: 600, color: t.text }}>
          {SOURCE_LABELS[source] ?? source}
        </td>
        <td style={td}>
          {latest ? fmtDate(latest.startedAt) : <span style={{ color: t.text3 }}>No runs yet</span>}
        </td>
        <td style={td}>
          {latest ? <StatusBadge status={latest.status} isDark={isDark} /> : "—"}
        </td>
        <td style={{ ...td, fontVariantNumeric: "tabular-nums" }}>
          {latest?.recordsUpserted != null ? latest.recordsUpserted : "—"}
        </td>
        <td style={{ ...td, fontVariantNumeric: "tabular-nums" }}>
          {latest ? fmtDuration(latest.durationSeconds) : "—"}
        </td>
        <td style={{ ...td, textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
          {(() => {
            const isDisabled = syncing === source || latest?.status === "IN_PROGRESS";
            return (
              <button
                onClick={() => onSyncNow(source)}
                disabled={isDisabled}
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 10, fontWeight: 600, letterSpacing: ".06em",
                  textTransform: "uppercase",
                  padding: "4px 12px", borderRadius: 4, cursor: isDisabled ? "not-allowed" : "pointer",
                  border: `1px solid ${t.accent}44`,
                  background: isDisabled
                    ? (isDark ? "rgba(40,179,180,0.06)" : "rgba(40,179,180,0.04)")
                    : (isDark ? "rgba(40,179,180,0.12)" : "rgba(40,179,180,0.08)"),
                  color: isDisabled ? t.text3 : t.accent,
                  display: "inline-flex", alignItems: "center", gap: 6,
                  transition: "background .15s",
                }}
              >
                {isDisabled && <CircularProgress size={8} sx={{ color: "inherit" }} />}
                {isDisabled ? "Syncing…" : "Sync Now"}
              </button>
            );
          })()}
        </td>
      </tr>

      {/* Expanded: last 5 runs sub-table */}
      {expanded && (
        <tr>
          <td colSpan={7} style={{ padding: 0 }}>
            <Box sx={{
              background: isDark ? "rgba(0,0,0,0.15)" : "rgba(3,81,77,0.03)",
              borderBottom: `1px solid ${t.border}`,
            }}>
              {runs.length === 0 ? (
                <Typography sx={{ padding: "12px 44px", fontSize: 12, color: t.text3 }}>
                  No sync runs recorded yet.
                </Typography>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, paddingLeft: 44 }}>Started At</th>
                      <th style={th}>Status</th>
                      <th style={th}>Records</th>
                      <th style={th}>Duration</th>
                      <th style={{ ...th, width: "40%" }}>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((r) => (
                      <tr key={r.syncId}>
                        <td style={{ ...td, paddingLeft: 44, color: t.text2, fontFamily: "'DM Mono', monospace", fontSize: 11 }}>
                          {fmtDate(r.startedAt)}
                        </td>
                        <td style={td}><StatusBadge status={r.status} isDark={isDark} /></td>
                        <td style={{ ...td, fontVariantNumeric: "tabular-nums" }}>
                          {r.recordsUpserted != null ? r.recordsUpserted : "—"}
                        </td>
                        <td style={{ ...td, fontVariantNumeric: "tabular-nums" }}>
                          {fmtDuration(r.durationSeconds)}
                        </td>
                        <td style={{ ...td, color: t.text3, fontSize: 11, wordBreak: "break-word" }}>
                          {r.errorMessage ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Box>
          </td>
        </tr>
      )}
    </>
  );
};

// ── Main SyncStatus component ─────────────────────────────────────────────────
const SyncStatus = () => {
  const theme    = useTheme();
  const t        = theme.custom;
  const isDark   = theme.palette.mode === "dark";
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [syncAll, setSyncAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const rows = await fetchSyncLogs();
      setData(rows);
      setError(null);
    } catch (e) {
      setError(e?.message ?? "Failed to load sync status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [load]);

  const handleSyncNow = async (source) => {
    if (source === "snapshot") {
      const confirmed = window.confirm(
        "This will capture a monthly snapshot of all associates.\n\n" +
        "This should only be run manually when needed — it is NOT part of the regular sync cycle.\n\n" +
        "Are you sure you want to proceed?"
      );
      if (!confirmed) return;
    }
    setSyncing(source);
    try {
      await triggerSync(source);
      setTimeout(load, 2000);
    } catch (e) {
      // silently log — next poll will show FAILED status
    } finally {
      setSyncing(null);
    }
  };

  const SYNC_ALL_ORDER = ["macs", "accounts", "sows", "employees", "contractors"];

  const handleSyncAll = async () => {
    setSyncAll(true);
    for (const source of SYNC_ALL_ORDER) {
      setSyncing(source);
      try {
        await triggerSync(source);
        await new Promise((res) => setTimeout(res, 1500));
      } catch (_) {}
    }
    setSyncing(null);
    setSyncAll(false);
    setTimeout(load, 2000);
  };

  const bySource = SOURCE_ORDER.reduce((acc, source) => {
    const found = data.find((d) => d.source === source);
    acc[source] = found?.recentRuns ?? [];
    return acc;
  }, {});

  const th = {
    fontSize: 10, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase",
    color: t.text3, padding: "10px 12px", textAlign: "left",
    borderBottom: `1.5px solid ${t.border2}`, whiteSpace: "nowrap",
    background: t.surface, position: "sticky", top: 0, zIndex: 2,
  };

  return (
    <Box sx={{ padding: "28px 28px 40px", display: "flex", flexDirection: "column", gap: "20px", flex: 1, overflowY: "auto" }}>

      {/* Back to dashboard */}
      <BackToDashboard />

      {/* Dependency order info banner */}
      <Box sx={{
        background: isDark ? "rgba(214,223,39,0.06)" : "rgba(214,223,39,0.06)",
        border: `1px solid ${isDark ? "rgba(214,223,39,0.25)" : "rgba(108,116,0,0.2)"}`,
        borderRadius: "8px", padding: "12px 16px",
        display: "flex", gap: "12px", alignItems: "flex-start",
      }}>
        <span style={{ fontSize: 14, marginTop: 1 }}>ℹ</span>
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: isDark ? "#D6DF27" : "#6B7300", mb: "4px" }}>
            Manual sync order
          </Typography>
          <Typography sx={{ fontSize: 12, color: t.text2, lineHeight: 1.6 }}>
            When triggering manually, sync in this order:{" "}
            <strong style={{ color: t.text }}>MACs → Accounts → SOWs</strong>.{" "}
            Accounts reference MACs (FK), and SOWs reference both MACs and Accounts.
            Employees and Contractors are independent — sync them in any order.
          </Typography>
        </Box>
      </Box>

      {/* Card */}
      <Box sx={{
        background: t.surface, border: `1px solid ${t.border}`,
        borderRadius: "12px", display: "flex", flexDirection: "column",
      }}>
        {/* Card header */}
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 18px", borderBottom: `1px solid ${t.border}`,
          borderRadius: "12px 12px 0 0", background: t.surface,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: t.text }}>
              Sync Status
            </Typography>
            <Box sx={{
              background: isDark ? "rgba(40,179,180,0.15)" : "rgba(40,179,180,0.1)",
              border: `1px solid ${t.accent}33`, borderRadius: "20px",
              padding: "2px 8px",
            }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: t.accent, letterSpacing: ".06em" }}>
                LIVE · 15s
              </Typography>
            </Box>
          </Box>

          <button
            onClick={handleSyncAll}
            disabled={syncAll || syncing != null}
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 10, fontWeight: 700, letterSpacing: ".06em",
              textTransform: "uppercase", padding: "6px 14px", borderRadius: 4,
              cursor: (syncAll || syncing != null) ? "not-allowed" : "pointer",
              border: `1px solid ${t.gold}55`,
              background: isDark ? "rgba(214,223,39,0.08)" : "rgba(214,223,39,0.06)",
              color: (syncAll || syncing != null) ? t.text3 : t.gold,
              display: "inline-flex", alignItems: "center", gap: 6,
              transition: "background .15s",
            }}
          >
            {syncAll && <CircularProgress size={8} sx={{ color: "inherit" }} />}
            {syncAll ? "Syncing All…" : "Sync All"}
          </button>
        </Box>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: "40px" }}>
            <CircularProgress size={24} sx={{ color: t.accent }} />
          </Box>
        ) : error ? (
          <Typography sx={{ padding: "24px", fontSize: 12, color: "#E24B4A" }}>{error}</Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 28 }} />
                  <th style={{ ...th, width: "18%" }}>Source</th>
                  <th style={{ ...th, width: "22%" }}>Last Run</th>
                  <th style={{ ...th, width: "14%" }}>Status</th>
                  <th style={{ ...th, width: "12%" }}>Records</th>
                  <th style={{ ...th, width: "10%" }}>Duration</th>
                  <th style={{ ...th, width: "16%", textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {SOURCE_ORDER.map((source) => (
                  <SourceRow
                    key={source}
                    source={source}
                    runs={bySource[source]}
                    isDark={isDark}
                    t={t}
                    onSyncNow={handleSyncNow}
                    syncing={syncing}
                  />
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SyncStatus;
